import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Users2, MessageCircle, Boxes, Wrench, Monitor, FileText,
  Megaphone, Image, UserCog, ScrollText, Settings as SettingsIcon, LogOut, Menu, X, Bell, ExternalLink,
  FileStack, ClipboardList, Wallet, Receipt, LifeBuoy, KeyRound,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/crm", label: "CRM & Devis", icon: Users2 },
  { to: "/admin/finance", label: "Finance", icon: Wallet },
  { to: "/admin/factures-clients", label: "Factures clients", icon: Receipt },
  { to: "/admin/support", label: "Support", icon: LifeBuoy },
  { to: "/admin/espace-client", label: "Accès clients", icon: KeyRound },
  { to: "/admin/whatsapp", label: "WhatsApp", icon: MessageCircle },
  { to: "/admin/solutions", label: "Solutions", icon: Boxes },
  { to: "/admin/services", label: "Services", icon: Wrench },
  { to: "/admin/produits", label: "Produits", icon: Monitor },
  { to: "/admin/blog", label: "Blog", icon: FileText },
  { to: "/admin/marketing", label: "Marketing", icon: Megaphone },
  { to: "/admin/formulaires", label: "Formulaires", icon: ClipboardList },
  { to: "/admin/mediatheque", label: "Médiathèque", icon: Image },
  { to: "/admin/pages", label: "Pages", icon: FileStack },
  { to: "/admin/utilisateurs", label: "Utilisateurs", icon: UserCog },
  { to: "/admin/journal", label: "Journal", icon: ScrollText },
  { to: "/admin/parametres", label: "Paramètres", icon: SettingsIcon },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);

  useEffect(() => { if (!loading && !user) navigate("/admin/login"); }, [user, loading, navigate]);
  useEffect(() => {
    if (user) api.get("/admin/notifications").then(({ data }) => setNotifs(data)).catch(() => {});
  }, [user]);

  if (loading || !user) return <div className="min-h-screen grid place-items-center bg-[#0B0F17] text-slate-400">Chargement…</div>;

  const unread = notifs.filter((n) => !n.read).length;
  const markRead = () => { api.post("/admin/notifications/read").catch(() => {}); setNotifs(notifs.map((n) => ({ ...n, read: true }))); };

  return (
    <div className="dark admin-scope min-h-screen bg-[#0B0F17] text-slate-100 flex">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 z-40 h-screen w-64 bg-[#070A10] border-r border-[#1E293B] flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-[70px] flex items-center px-5 border-b border-[#1E293B]">
          <img src="/logo.webp" alt="VISION" className="h-8 w-auto bg-white rounded-md p-1" />
          <span className="ml-2 text-xs font-mono text-slate-500 uppercase tracking-wider">Admin</span>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={`sidebar-${n.to.split("/")[2] || "dashboard"}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-[#3B82F6] text-white" : "text-slate-400 hover:text-white hover:bg-[#111827]"}`}>
              <n.icon className="w-4.5 h-4.5 w-5 h-5" />{n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#1E293B]">
          <div className="px-3 py-2">
            <div className="text-sm font-semibold text-white truncate">{user.name}</div>
            <div className="text-xs text-slate-500 capitalize">{user.role}</div>
          </div>
          <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-slate-400 hover:text-white hover:bg-[#111827]">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-[70px] sticky top-0 z-20 bg-[#0B0F17]/80 backdrop-blur-xl border-b border-[#1E293B] flex items-center justify-between px-4 sm:px-6">
          <button className="lg:hidden p-2 text-slate-300" onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
          <div className="hidden sm:block text-sm text-slate-400">Bienvenue, <span className="text-white font-medium">{user.name}</span></div>
          <div className="flex items-center gap-2 ml-auto">
            <Link to="/" target="_blank" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white px-3 py-2 rounded-lg hover:bg-[#111827]"><ExternalLink className="w-4 h-4" /><span className="hidden sm:inline">Voir le site</span></Link>
            <div className="relative">
              <button onClick={() => { setShowNotifs(!showNotifs); if (!showNotifs) markRead(); }} data-testid="notifications-button" className="relative p-2 rounded-lg text-slate-400 hover:text-white hover:bg-[#111827]">
                <Bell className="w-5 h-5" />
                {unread > 0 && <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-[#F43F5E] text-white text-[10px] grid place-items-center">{unread}</span>}
              </button>
              {showNotifs && (
                <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-xl bg-[#111827] border border-[#1E293B] shadow-2xl p-2 z-50">
                  {notifs.length === 0 && <div className="p-4 text-sm text-slate-500 text-center">Aucune notification</div>}
                  {notifs.slice(0, 20).map((n) => (
                    <div key={n.id} className="p-3 rounded-lg hover:bg-[#0B0F17]">
                      <div className="text-sm font-medium text-white">{n.title}</div>
                      <div className="text-xs text-slate-400">{n.message}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden"><Outlet /></main>
      </div>
    </div>
  );
}
